import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, Mail, LogOut, Plus, Menu, X, ArrowLeftRight, Gift, ShieldCheck } from "lucide-react";import { axios, API, logError } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";
import { TierBadge } from "./Badges";
import { ToyCycleLogo } from "./Logo";
import { NotificationsBell } from "./NotificationsBell";

const POLL_INTERVAL_MS = 15000;

/**
 * Top-level header. Slimmed by extracting the desktop nav, mobile drawer, and
 * unread-message polling into focused subcomponents/hook.
 */
export const Header = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const unreadCount = useUnreadCount(user);

  return (
    <header
      className="bg-[#FDFBF7] border-b-2 border-gray-900 py-4 px-6 sticky top-0 z-50"
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <BrandLink />
        <SearchForm value={searchQuery} onChange={setSearchQuery} />
        <DesktopNav user={user} unreadCount={unreadCount} onLogout={logout} />
        <button
          className="md:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle navigation"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <MobileDrawer
          user={user}
          unreadCount={unreadCount}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onLogout={logout}
          onClose={() => setMobileMenuOpen(false)}
        />
      )}
    </header>
  );
};

// ----- subcomponents --------------------------------------------------------

const BrandLink = () => (
  <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
    <ToyCycleLogo size={42} />
    <span
      className="font-black text-xl tracking-tight hidden sm:block"
      style={{ fontFamily: "Outfit" }}
    >
      <span className="text-gray-900">Toy</span>
      <span className="text-[#FF6B6B]">Cycle</span>
      <span className="text-[#4A90E2]">Hub</span>
    </span>
  </Link>
);

const SearchForm = ({ value, onChange, mobile }) => {
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    if (value.trim()) navigate(`/browse?search=${encodeURIComponent(value)}`);
  };
  return (
    <form
      onSubmit={submit}
      className={mobile ? "mb-4" : "flex-1 max-w-xl hidden md:block"}
    >
      <div className="relative">
        <input
          type="text"
          placeholder={mobile ? "Search..." : "Search for toys..."}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="brutalist-input pr-12"
          data-testid={mobile ? "search-input-mobile" : "search-input"}
        />
        {!mobile && (
          <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2">
            <Search className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>
    </form>
  );
};

const DesktopNav = ({ user, unreadCount, onLogout }) => (
  <nav className="hidden md:flex items-center gap-4">
    <NavLinks />
    {user ? (
      <SignedInActions user={user} unreadCount={unreadCount} onLogout={onLogout} />
    ) : (
      <Link
        to="/auth"
        className="brutalist-btn-primary text-sm py-2 px-6"
        data-testid="login-link"
      >
        Sign In
      </Link>
    )}
  </nav>
);

const NavLinks = () => (
  <>
    <Link to="/marketplace" className="font-bold text-sm uppercase tracking-wide hover:text-[#FF6B6B]" data-testid="marketplace-link">Marketplace</Link>
    <Link to="/browse?transaction_type=sell" className="font-bold text-sm uppercase tracking-wide hover:text-[#FF6B6B] flex items-center gap-1" data-testid="buy-link"><ShoppingCart className="w-4 h-4" />Buy</Link>
    <Link to="/browse?transaction_type=swap" className="font-bold text-sm uppercase tracking-wide hover:text-[#FF6B6B] flex items-center gap-1" data-testid="swap-link"><ArrowLeftRight className="w-4 h-4" />Swap</Link>
    <Link to="/browse?is_donation=true" className="font-bold text-sm uppercase tracking-wide hover:text-[#FF6B6B] flex items-center gap-1" data-testid="free-link"><Gift className="w-4 h-4" />Free</Link>
    <Link to="/pricing" className="font-bold text-sm uppercase tracking-wide hover:text-[#FF6B6B]" data-testid="pricing-link">Pricing</Link>
    <Link to="/trust" className="font-bold text-sm uppercase tracking-wide hover:text-[#FF6B6B] flex items-center gap-1" data-testid="trust-link"><ShieldCheck className="w-4 h-4" />Trust</Link>
  </>
);

const SignedInActions = ({ user, unreadCount, onLogout }) => (
  <>
    <Link to="/create-listing" className="brutalist-btn-secondary text-sm py-2 px-4" data-testid="sell-list-button">
      <Plus className="w-4 h-4 inline mr-1" />Sell
    </Link>
    <Link
      to="/referrals"
      className="font-bold text-sm uppercase tracking-wide hover:text-[#FF6B6B] hidden lg:flex items-center gap-1"
      data-testid="refer-link"
    >
      <Gift className="w-4 h-4" />Refer £1
    </Link>
    <MessagesLink unreadCount={unreadCount} />
    <NotificationsBell user={user} />
    <Link
      to="/dashboard"
      data-testid="dashboard-link"
      className="font-bold text-sm uppercase tracking-wide hover:text-[#FF6B6B] flex items-center gap-2"
    >
      Dashboard
      {user.subscription_tier && user.subscription_tier !== "basic" && (
        <TierBadge tier={user.subscription_tier} />
      )}
    </Link>
    <button onClick={onLogout} className="p-2 hover:text-[#FF6B6B]" data-testid="logout-button">
      <LogOut className="w-5 h-5" />
    </button>
  </>
);

const MessagesLink = ({ unreadCount, mobile, onClick }) => (
  <Link
    to="/messages"
    className={mobile ? "font-bold uppercase flex items-center gap-2" : "relative p-2 hover:text-[#FF6B6B]"}
    onClick={onClick}
    data-testid={mobile ? "messages-link-mobile" : "messages-link"}
    aria-label={`Messages${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
  >
    {!mobile && <Mail className="w-5 h-5" />}
    {mobile && "Messages"}
    {unreadCount > 0 && (
      <span
        className={
          mobile
            ? "bg-[#FF6B6B] text-white text-[10px] font-black px-2 py-0.5 rounded-full border-2 border-gray-900"
            : "absolute -top-1 -right-1 bg-[#FF6B6B] text-white text-[10px] font-black w-5 h-5 rounded-full border-2 border-gray-900 flex items-center justify-center"
        }
        data-testid={mobile ? "messages-unread-badge-mobile" : "messages-unread-badge"}
      >
        {unreadCount > 9 ? "9+" : unreadCount}
      </span>
    )}
  </Link>
);

const MobileDrawer = ({ user, unreadCount, searchQuery, onSearchChange, onLogout, onClose }) => (
  <div className="md:hidden mt-4 pb-4 border-t-2 border-gray-900 pt-4">
    <SearchForm value={searchQuery} onChange={onSearchChange} mobile />
    <div className="flex flex-col gap-3">
      <Link to="/marketplace" className="font-bold uppercase" onClick={onClose}>Marketplace</Link>
      <Link to="/browse?transaction_type=sell" className="font-bold uppercase" onClick={onClose}>Buy</Link>
      <Link to="/browse?transaction_type=swap" className="font-bold uppercase" onClick={onClose}>Swap</Link>
      <Link to="/browse?is_donation=true" className="font-bold uppercase" onClick={onClose}>Free Items</Link>
      <Link to="/pricing" data-testid="pricing-link-mobile" className="font-bold uppercase" onClick={onClose}>Pricing</Link>
      <Link to="/trust" data-testid="trust-link-mobile" className="font-bold uppercase" onClick={onClose}>Trust &amp; Safety</Link>
      {user ? (
        <>
          <Link to="/create-listing" className="font-bold uppercase" onClick={onClose}>Sell a Toy</Link>
          <MessagesLink unreadCount={unreadCount} mobile onClick={onClose} />
          <Link to="/dashboard" className="font-bold uppercase" onClick={onClose}>Dashboard</Link>
          <Link to="/referrals" data-testid="refer-link-mobile" className="font-bold uppercase" onClick={onClose}>Refer &amp; Earn £1</Link>
          <button
            onClick={() => { onLogout(); onClose(); }}
            className="text-left font-bold uppercase text-[#FF6B6B]"
          >
            Logout
          </button>
        </>
      ) : (
        <Link to="/auth" className="brutalist-btn-primary text-center" onClick={onClose}>Sign In</Link>
      )}
    </div>
  </div>
);

// ----- hook -----------------------------------------------------------------

const useUnreadCount = (user) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!user) {
      setCount(0);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/messages/unread-count`);
        if (!cancelled) setCount(res.data?.unread || 0);
      } catch (err) {
        // 401 here just means the cookie expired between polls — don't spam logs.
        if (err?.response?.status !== 401) logError("Header: unread-count failed", err);
      }
    };
    load();
    const i = setInterval(load, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(i); };
  }, [user]);
  return count;
};
